import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, Settings, Move, Check, X, Save } from 'lucide-react';
import FieldEditor from './FieldEditor';
import FormSettings from './FormSettings';

const FormEditor = ({ form, onSave, onCancel }) => {
  const [formData, setFormData] = useState(form || {
    name: 'Untitled Form',
    description: '',
    fields: [],
    settings: {
      whatsappNumber: '',
      messageTemplate: 'New form submission:\n\n{{formData}}',
      redirectUrl: '',
      theme: 'default'
    }
  });
  const [activeTab, setActiveTab] = useState('fields'); // fields, settings
  const [draggedField, setDraggedField] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (form) {
      setFormData(form);
    }
  }, [form]);

  // Handle form name and description changes
  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Add a new field
  const addField = (fieldType) => {
    const newField = createDefaultField(fieldType);
    setFormData({
      ...formData,
      fields: [...formData.fields, newField]
    });
  };

  // Create a default field based on type
  const createDefaultField = (type) => {
    const id = `field_${Date.now()}`;
    const baseField = {
      id,
      type,
      label: getDefaultLabel(type),
      placeholder: `Enter ${getDefaultLabel(type).toLowerCase()}`,
      required: false
    };

    // Add type-specific properties
    switch (type) {
      case 'select':
        return {
          ...baseField,
          options: ['Option 1', 'Option 2', 'Option 3'],
          placeholder: 'Select an option'
        };
      case 'radio':
        return {
          ...baseField,
          options: ['Option 1', 'Option 2', 'Option 3']
        };
      case 'checkbox':
        return {
          ...baseField,
          options: ['Option 1', 'Option 2', 'Option 3']
        };
      default:
        return baseField;
    }
  };

  // Get default label based on field type
  const getDefaultLabel = (type) => {
    switch (type) {
      case 'text':
        return 'Text Field';
      case 'email':
        return 'Email Address';
      case 'tel':
        return 'Phone Number';
      case 'number':
        return 'Number';
      case 'textarea':
        return 'Text Area';
      case 'select':
        return 'Dropdown';
      case 'radio':
        return 'Radio Buttons';
      case 'checkbox':
        return 'Checkboxes';
      case 'date':
        return 'Date';
      default:
        return 'Field';
    }
  };

  // Update a field
  const updateField = (fieldId, updatedField) => {
    setFormData({
      ...formData,
      fields: formData.fields.map(field => 
        field.id === fieldId ? { ...field, ...updatedField } : field
      )
    });
  };

  // Delete a field
  const deleteField = (fieldId) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter(field => field.id !== fieldId)
    });
  };

  // Handle field drag start
  const handleDragStart = (e, fieldId) => {
    setDraggedField(fieldId);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    // Use a transparent image as drag ghost
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
  };

  // Handle field drag over
  const handleDragOver = (e, fieldId) => {
    e.preventDefault();
    if (draggedField === fieldId) return;
    
    const draggedIndex = formData.fields.findIndex(field => field.id === draggedField);
    const targetIndex = formData.fields.findIndex(field => field.id === fieldId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    const newFields = [...formData.fields];
    const [removed] = newFields.splice(draggedIndex, 1);
    newFields.splice(targetIndex, 0, removed);
    
    setFormData({
      ...formData,
      fields: newFields
    });
  };

  // Handle field drag end
  const handleDragEnd = () => {
    setDraggedField(null);
    setIsDragging(false);
  };

  // Update form settings
  const updateSettings = (newSettings) => {
    setFormData({
      ...formData,
      settings: {
        ...formData.settings,
        ...newSettings
      }
    });
  };

  // Save the form
  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center mb-6">
          <button
            onClick={onCancel}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900">
            {form?.id ? 'Edit Form' : 'Create Form'}
          </h2>
          <button
            onClick={handleSave}
            className="ml-auto bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 flex items-center"
          >
            <Save className="h-5 w-5 mr-1" />
            Save Form
          </button>
        </div>

        {/* Form Basic Info */}
        <div className="mb-6">
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Form Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleBasicInfoChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter form name"
            />
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleBasicInfoChange}
              rows="2"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter form description"
            ></textarea>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('fields')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'fields'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Form Fields
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Settings
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'fields' ? (
          <div>
            {/* Field List */}
            <div className="mb-6">
              {formData.fields.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500 mb-4">No fields added yet</p>
                  <p className="text-sm text-gray-500">
                    Add fields using the buttons below
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.fields.map((field, index) => (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, field.id)}
                      onDragOver={(e) => handleDragOver(e, field.id)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white border rounded-md ${
                        draggedField === field.id ? 'opacity-50' : ''
                      } ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                    >
                      <FieldEditor
                        field={field}
                        onUpdate={(updatedField) => updateField(field.id, updatedField)}
                        onDelete={() => deleteField(field.id)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Field Buttons */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Add Field</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  onClick={() => addField('text')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Text Field
                </button>
                <button
                  onClick={() => addField('email')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Email
                </button>
                <button
                  onClick={() => addField('tel')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Phone
                </button>
                <button
                  onClick={() => addField('number')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Number
                </button>
                <button
                  onClick={() => addField('textarea')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Text Area
                </button>
                <button
                  onClick={() => addField('select')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Dropdown
                </button>
                <button
                  onClick={() => addField('radio')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Radio Buttons
                </button>
                <button
                  onClick={() => addField('checkbox')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Checkboxes
                </button>
                <button
                  onClick={() => addField('date')}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Date
                </button>
              </div>
            </div>
          </div>
        ) : (
          <FormSettings settings={formData.settings} onUpdate={updateSettings} />
        )}
      </div>
    </div>
  );
};

export default FormEditor;
